import * as React from 'react'
import { vsprintf } from 'sprintf-js'

import { i18n, MessageArea } from '@thg-commerce/enterprise-core'
import { BasketData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Basket/Basket'
import { AriaMessageType } from '@thg-commerce/gravity-elements'
import { Margin, TextStyling } from '@thg-commerce/gravity-theme'

import { ProgressBar } from '../ProgressBar'

import { Product } from './Product'
import {
  QualifiedContainer,
  QualifiedText,
  SelectedGifts,
  StyledIconSuccess,
  StyledPlatformMessage,
  TierContainer,
} from './styles'

export const Qualified = (props: {
  text: string
  thresholdAmountSpent?: string
}) => {
  return (
    <QualifiedContainer>
      <StyledIconSuccess />
      <QualifiedText>
        {props.text}
        {props.thresholdAmountSpent && ` ${props.thresholdAmountSpent}`}
      </QualifiedText>
    </QualifiedContainer>
  )
}

const renderAnnouncer = (ariaLive: AriaMessageType, message: string) => {
  return <MessageArea aria-live={ariaLive} message={message} />
}

export interface TierProps {
  id: string
  products: BasketData['selectYourSample'][0]['tiers'][0]['products']
  isFullyOutOfStock: boolean
  isPartiallyOutOfStock: boolean
  selectedProducts: BasketData['selectYourSample'][0]['tiers'][0]['selectedProducts']
  currentAmountSpent: number
  additionalSpendToReach: {
    amount: number
    displayValue: string
  }
  maxSelectedProducts: number
  thresholdAmountSpent?: string | false
  enableGiftBasketLayout?: boolean
  selectedGiftsTheme?: {
    textStyling?: TextStyling
    margin?: Margin
  }
  tierContainerTheme?: {
    margin?: Margin
  }
}

export const Tier = (props: TierProps) => {
  const i18nText = {
    selectedGifts: i18n('basket.selectyoursample.gifts.selected.text'),
    qualified: i18n('general.qualified.text'),
    thresholdAmountSpentText: i18n(
      'basket.selectyoursample.tier.threshold.text',
    ),
    fullyOutOfStockText: i18n('basket.selectyoursample.tier.oos.text'),
    partiallyOutOfStockText: i18n('basket.selectyoursample.oos.text'),
  }

  const meta = !props.additionalSpendToReach.amount ? (
    <React.Fragment>
      {!props.enableGiftBasketLayout && (
        <Qualified
          text={i18nText.qualified}
          thresholdAmountSpent={
            props.thresholdAmountSpent
              ? vsprintf(i18nText.thresholdAmountSpentText, [
                  props.thresholdAmountSpent,
                ])
              : undefined
          }
        />
      )}
      <SelectedGifts
        data-testid="qualified"
        enableGiftBasketLayout={props.enableGiftBasketLayout}
        textStyling={props.selectedGiftsTheme?.textStyling}
        margin={props.selectedGiftsTheme?.margin}
      >
        {vsprintf(i18nText.selectedGifts, [
          props.selectedProducts.length,
          props.maxSelectedProducts,
        ])}
      </SelectedGifts>
      {props.isFullyOutOfStock && (
        <StyledPlatformMessage
          data-testid="fully-out-of-stock-message"
          type="info"
          text={i18nText.fullyOutOfStockText}
          renderAnnouncer={renderAnnouncer}
          iconBoxWidth={20}
        />
      )}
      {props.isPartiallyOutOfStock && (
        <StyledPlatformMessage
          data-testid="partially-out-of-stock-message"
          type="info"
          text={i18nText.partiallyOutOfStockText}
          renderAnnouncer={renderAnnouncer}
          iconBoxWidth={20}
        />
      )}
    </React.Fragment>
  ) : (
    <React.Fragment>
      <ProgressBar
        additionalSpendToReach={{
          amount: props.additionalSpendToReach.amount,
          displayValue: props.additionalSpendToReach.displayValue,
        }}
        currentAmountSpent={props.currentAmountSpent}
        isOpen
      />
    </React.Fragment>
  )

  return (
    <TierContainer
      enableGiftBasketLayout={props.enableGiftBasketLayout}
      margin={props.tierContainerTheme?.margin}
    >
      {meta}
      <Product
        products={props.products}
        selectedProducts={props.selectedProducts}
        tierId={props.id}
        isQualified={!props.additionalSpendToReach.amount}
        isSelectable={
          props.selectedProducts.length !== props.maxSelectedProducts
        }
        enableGiftBasketLayout={props.enableGiftBasketLayout}
      />
    </TierContainer>
  )
}
